import React, { useState } from "react";
import { Sparkles, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Public "Hello" handshake. Anyone can visit /meet — no auth.
 * ME introduces itself, asks for a name (with explicit consent), and
 * creates a guest Person row.
 */
export default function MeetPage() {
  const { t, language } = useLanguage();
  const [step, setStep] = useState("intro"); // intro → name → done
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/persons/introduce", {
        display_name: name || null,
        language,
        channel: "web",
        consent_remember_name: !!consent,
      });
      setResult(data);
      setStep("done");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12"
      data-testid="meet-page"
    >
      <div className="w-full max-w-lg space-y-6">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <span className="text-lg font-semibold tracking-tight">ME</span>
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">
            {t.meet.title}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t.meet.sub}</p>
        </div>

        {step === "intro" && (
          <div className="mios-card" data-testid="meet-intro-card">
            <div className="mios-card-body space-y-4">
              <p className="text-base leading-relaxed text-slate-700">{t.meet.greeting}</p>
              <button
                type="button"
                data-testid="meet-continue-btn"
                onClick={() => setStep("name")}
                className="mios-btn mios-btn-primary w-full"
              >
                <Sparkles className="h-4 w-4" />
                {t.meet.continue}
              </button>
            </div>
          </div>
        )}

        {step === "name" && (
          <div className="mios-card" data-testid="meet-name-card">
            <div className="mios-card-body space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {t.meet.askName}
                </div>
                <input
                  data-testid="meet-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.meet.namePlaceholder}
                  className="mios-input mt-1"
                  autoFocus
                />
              </div>

              <label className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="meet-consent-checkbox"
                />
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />
                  <span>{t.meet.consentLine}</span>
                </div>
              </label>

              {error && <div className="text-sm text-red-700">{error}</div>}

              <button
                type="button"
                disabled={busy}
                onClick={submit}
                data-testid="meet-submit-btn"
                className="mios-btn mios-btn-primary w-full"
              >
                {busy ? t.meet.greeting2 : t.meet.submit}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConsent(false);
                  setName("");
                  submit();
                }}
                disabled={busy}
                data-testid="meet-skip-btn"
                className="mios-btn mios-btn-ghost w-full"
              >
                {t.meet.skipName}
              </button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="mios-card" data-testid="meet-done-card">
            <div className="mios-card-body space-y-3">
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.meet.meSays}
              </div>
              <p
                className="whitespace-pre-wrap text-base leading-relaxed text-slate-800"
                data-testid="meet-greeting-text"
              >
                {result.greeting}
              </p>
              {result.person && (
                <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                    {t.meet.personCreated}
                  </div>
                  <div className="mt-1 mono text-slate-800">{result.person.id}</div>
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  setStep("intro");
                  setName("");
                  setConsent(false);
                  setResult(null);
                }}
                data-testid="meet-restart-btn"
                className="mios-btn mios-btn-ghost w-full"
              >
                {t.meet.restart}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
